import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import MainBodyContainer from "../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../Footer/Footer";
import "./Contact.css";

const Contact = () => {
  const formRef = useRef(null);
  const nameInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    purpose: "",
    urgency: "standard",
    name: "",
    email: "",
    phone: "",
    details: "",
    courseInterest: "",
    employmentStatus: "",
    privacyAgreed: false
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  const purposes = [
    { id: "enroll", label: "Enroll in a Course", icon: "🎓", description: "ESOL, Functional Skills & more" },
    { id: "employment", label: "Career & Employment Support", icon: "💼", description: "CV help, job search, interviews" },
    { id: "advocacy", label: "Advocacy & Rights Support", icon: "⚖️", description: "Housing, benefits, legal aid" },
    { id: "community", label: "Community Services", icon: "🤝", description: "Local support & resources" },
    { id: "volunteer", label: "Volunteer with APT", icon: "🙋", description: "Join our team" },
    { id: "partnership", label: "Partnership Inquiry", icon: "🤝", description: "Work with us" },
    { id: "media", label: "Media & Press", icon: "📰", description: "Press enquiries" },
    { id: "general", label: "General Inquiry", icon: "❓", description: "Other questions" }
  ];

  const employmentOptions = [
    { value: "", label: "Select your current situation" },
    { value: "unemployed", label: "Currently unemployed" },
    { value: "part-time", label: "Working part-time" },
    { value: "full-time", label: "Working full-time" },
    { value: "student", label: "Student" },
    { value: "retired", label: "Retired" },
    { value: "other", label: "Other" }
  ];

  const faqs = [
    {
      question: "Do I need to speak English to get help?",
      answer: "No. We provide support in English, Albanian, Arabic, Bengali, Urdu, and Tigrinya. If you need an interpreter for other languages, please let us know and we'll arrange one."
    },
    {
      question: "What documents do I need to bring?",
      answer: "For most inquiries, just bring photo ID. For benefit advocacy, bring any letters from DWP or council. For course enrollment, bring proof of address. Don't worry if you're missing documents—we can help you obtain them."
    },
    {
      question: "Is there a cost for your services?",
      answer: "All our services are completely free. We are a Community Interest Company funded to support local residents. There are no hidden fees or charges."
    },
    {
      question: "How quickly will you respond to my message?",
      answer: "Urgent advocacy (housing crisis, benefit stoppage): Same business day | Course enrollment: 2-3 business days | Employment support: 2-3 business days | General inquiries: 3-5 business days. Office hours: Mon-Fri, 9am-5pm."
    },
    {
      question: "Will my information be kept private?",
      answer: "Yes. We follow strict data protection rules (GDPR). Your information is only shared with your consent or when legally required. Read our full Privacy Policy for details."
    },
    {
      question: "Can someone help me fill out this form?",
      answer: "Absolutely. You can call us on 020 3026 6258 and we'll complete the form together over the phone. Or visit us during drop-in hours (Mon-Fri, 10am-4pm) and our staff will assist you."
    },
    {
      question: "What areas do you cover?",
      answer: "We primarily serve Newham, Enfield, Redbridge, Barking & Dagenham, and Havering. If you're outside these areas, we can refer you to appropriate local services."
    },
    {
      question: "I need urgent help with housing/benefits - what should I do?",
      answer: "For urgent matters: 1) Call us immediately on 020 3026 6258 (Mon-Fri, 9am-5pm), or 2) Select 'Advocacy & Rights Support' above and mark it as urgent. We'll respond the same business day."
    }
  ];

  const handlePurposeSelect = (purposeId) => {
    setFormData(prev => ({
      ...prev,
      purpose: purposeId,
      urgency: purposeId === "advocacy" ? prev.urgency : "standard"
    }));
    setErrors(prev => ({ ...prev, purpose: null }));
    
    // Scroll to form after a short delay to allow rendering
    setTimeout(() => {
      const formSection = document.querySelector('.two-column-layout');
      if (formSection) {
        formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Purpose
    if (!formData.purpose) {
      newErrors.purpose = "Please select why you're contacting us";
      isValid = false;
    }

    // Name
    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = "Please enter your full name (at least 2 characters)";
      isValid = false;
    } else if (!/^[a-zA-Z\s'-]{2,50}$/.test(formData.name)) {
      newErrors.name = "Name can only contain letters, spaces, hyphens and apostrophes";
      isValid = false;
    }

    // Email
    if (!formData.email) {
      newErrors.email = "Please enter your email address";
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
        isValid = false;
      }
    }

    // Phone (required for all)
    if (!formData.phone) {
      newErrors.phone = "Please enter your phone number";
      isValid = false;
    } else {
      // UK phone validation - accepts various formats
      const phoneRegex = /^(?:(?:\(?(?:0(?:0|11)\)?[\s-]?\(?|\+)44\)?[\s-]?(?:\(?0\)?[\s-]?)?)|(?:\(?0))(?:(?:\d{5}\)?[\s-]?\d{4,5})|(?:\d{4}\)?[\s-]?(?:\d{5}|\d{3}[\s-]?\d{3}))|(?:\d{3}\)?[\s-]?\d{3}[\s-]?\d{3,4})|(?:\d{2}\)?[\s-]?\d{4}[\s-]?\d{4}))(?:[\s-]?(?:x|ext\.?|#)\d{3,4})?$/;
      const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');
      if (!phoneRegex.test(formData.phone) && !/^\d{10,11}$/.test(cleanPhone)) {
        newErrors.phone = "Please enter a valid UK phone number";
        isValid = false;
      }
    }

    // Details
    if (!formData.details || formData.details.trim().length < 10) {
      newErrors.details = "Please provide more details (at least 10 characters)";
      isValid = false;
    }

    // Privacy agreement
    if (!formData.privacyAgreed) {
      newErrors.privacyAgreed = "You must agree to our privacy policy to continue";
      isValid = false;
    }

    // Conditional field validation
    if (formData.purpose === "employment" && !formData.employmentStatus) {
      newErrors.employmentStatus = "Please select your employment status";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`) || 
                          document.querySelector(`[data-field="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return;
    }

    setIsLoading(true);
    setSubmissionError(null);

    const selectedPurpose = purposes.find(p => p.id === formData.purpose);
    
    const requestData = {
      ...formData,
      purposeLabel: selectedPurpose?.label || formData.purpose,
      isUrgent: formData.purpose === "advocacy" && formData.urgency === "urgent",
      subject: formData.purpose === "advocacy" && formData.urgency === "urgent" 
        ? `[URGENT] ${selectedPurpose?.label}: ${formData.name}`
        : `${selectedPurpose?.label}: ${formData.name}`
    };

    try {
      const response = await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData)
      });

      if (response.status === 200) {
        setSubmissionSuccess(true);
        setFormData({
          purpose: "",
          urgency: "standard",
          name: "",
          email: "",
          phone: "",
          details: "",
          courseInterest: "",
          employmentStatus: "",
          privacyAgreed: false
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const data = await response.json();
        throw new Error(data.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Error:", error);
      setSubmissionError(error.message || "Something went wrong. Please try again or call us on 020 3026 6258.");
    } finally {
      setIsLoading(false);
    }
  };

  const getResponseTimeText = () => {
    if (formData.purpose === "advocacy" && formData.urgency === "urgent") {
      return "Same business day (Mon-Fri, 9am-5pm)";
    }
    switch (formData.purpose) {
      case "advocacy":
        return "1-2 business days";
      case "enroll":
        return "2-3 business days";
      case "employment":
        return "2-3 business days";
      default:
        return "3-5 business days";
    }
  };

  const isUrgent = formData.purpose === "advocacy" && formData.urgency === "urgent";

  if (submissionSuccess) {
    return (
      <div>
        <Navbar />
        <div className="contact-heading-wrapper">
          <header className="contact-heading-container">
            <h1>Thank You!</h1>
            <h3>Your message has been sent successfully</h3>
          </header>
        </div>
        <div className="index-container">
          <MainBodyContainer>
            <div className="success-page-container">
              <div className="success-icon">✓</div>
              <h2>We've received your message</h2>
              <p className="success-message-text">
                Thank you <strong>{formData.name}</strong>,
              </p>
              <p>
                We've received your inquiry about <strong>{purposes.find(p => p.id === formData.purpose)?.label}</strong>.
                {isUrgent ? (
                  <>
                    <br /><br />
                    <strong style={{ color: '#e74c3c' }}>This is marked as URGENT.</strong>
                    <br />
                    Our advocacy team will contact you today during office hours (9am-5pm).
                    If you don't hear from us by 5pm, please call us on <a href="tel:02030266258">020 3026 6258</a>.
                  </>
                ) : (
                  <>
                    <br /><br />
                    We aim to respond within <strong>{getResponseTimeText()}</strong>.
                  </>
                )}
              </p>
              
              <div className="success-next-steps">
                <h3>What happens next?</h3>
                <ul>
                  <li>📧 You'll receive a confirmation email shortly</li>
                  <li>📞 We'll call or email you to discuss your needs</li>
                  <li>📋 We'll guide you through the next steps</li>
                </ul>
              </div>

              <div className="success-contact-info">
                <p>
                  <strong>Need to speak to someone sooner?</strong>
                  <br />
                  Call us on <a href="tel:02030266258">020 3026 6258</a>
                  <br />
                  <small>Monday to Friday, 9:00 AM - 5:00 PM</small>
                </p>
              </div>

              <div className="success-actions">
                <button 
                  onClick={() => {
                    setSubmissionSuccess(false);
                    setFormData({
                      purpose: "",
                      urgency: "standard",
                      name: "",
                      email: "",
                      phone: "",
                      details: "",
                      courseInterest: "",
                      employmentStatus: "",
                      privacyAgreed: false
                    });
                  }}
                  className="secondary-button"
                >
                  Send Another Message
                </button>
                <Link to="/" className="primary-button">
                  Return to Homepage
                </Link>
              </div>
            </div>
          </MainBodyContainer>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div>
        {/* Colorful Header */}
        <div className="contact-heading-wrapper">
          <header className="contact-heading-container">
            <h1>Contact Us</h1>
            <h3>We're here to support you</h3>
          </header>
        </div>

        <div className="index-container">
          <MainBodyContainer>
            {/* Purpose Selector */}
            <section className="purpose-selector-section">
              <h2>How can we help you today?</h2>
              <p className="purpose-intro">Select the option that best describes why you're contacting us:</p>
              
              <div className="purpose-grid" data-field="purpose">
                {purposes.map((purpose) => (
                  <button
                    key={purpose.id}
                    type="button"
                    className={`purpose-card ${formData.purpose === purpose.id ? 'selected' : ''}`}
                    onClick={() => handlePurposeSelect(purpose.id)}
                  >
                    <span className="purpose-icon">{purpose.icon}</span>
                    <span className="purpose-label">{purpose.label}</span>
                    <span className="purpose-description">{purpose.description}</span>
                  </button>
                ))}
              </div>
              
              {errors.purpose && (
                <div className="error-message-inline">
                  <span className="error-icon">⚠️</span> {errors.purpose}
                </div>
              )}
            </section>

            {/* Always Visible Contact Options */}
            <section className="always-visible-contact">
              <div className="contact-options-grid">
                <section className="phone-support-card">
                  <div className="phone-support-container">
                    <div className="phone-support-text">
                      <div>
                        <h3>Give us a call</h3>
                        <p>
                          Speak directly with a member of our team to get answers to your questions.
                        </p>
                      </div>
                      <figure>
                        <i className="las la-mobile"></i>
                      </figure>
                    </div>

                    <ul className="phone-support-list">
                      <li className="phone-support-list-item">
                        <div className="phone-support-list-item-text">
                          <span>UK</span>
                          <a href="tel:+442030266258">+44 20 3026 6258</a>
                        </div>
                      </li>
                    </ul>
                  </div>
                </section>

                <section className="email-support-card">
                  <div className="email-support-container">
                    <h3>Email us directly</h3>
                    <a
                      className="email-contact"
                      href="mailto:info@alisarprospects.training"
                    >
                      info@alisarprospects.training
                    </a>
                    <p className="email-support-hours">
                      We typically respond within 1-3 business days
                    </p>
                  </div>
                </section>

                <section className="visit-support-card">
                  <div className="visit-support-container">
                    <h3>Visit us</h3>
                    <p className="visit-hours">
                      <strong>Drop-in hours:</strong><br />
                      Monday - Friday<br />
                      9:00 AM - 5:00 PM
                    </p>
                    <p className="visit-note">
                      Newham, Enfield, Redbridge, Barking & Dagenham, and Havering
                    </p>
                  </div>
                </section>
              </div>
            </section>

            {formData.purpose && (
              <div className="two-column-layout">
                {/* Main Column - Form */}
                <section className="main-col">
                  <section className="contact-us-container">
                    <section className="contact-us-text">
                      <h1>Get in Touch</h1>
                      <p>
                        Please fill in your details below. {isUrgent && (
                          <strong style={{ color: '#e74c3c' }}>This will be treated as urgent.</strong>
                        )}
                      </p>
                    </section>

                    <section className="contact-form-container">
                      {/* Response Time Indicator */}
                      <div className={`response-time-badge ${isUrgent ? 'urgent' : ''}`}>
                        <span className="response-time-label">Expected response time:</span>
                        <span className="response-time-value">{getResponseTimeText()}</span>
                      </div>

                      <section className="contact-form-section">
                        <form
                          ref={formRef}
                          onSubmit={handleSubmit}
                          id="contact-us-form"
                          noValidate
                        >
                          <section className="contact-form-dropdown-container">
                            {/* Urgency Selection (only for advocacy) */}
                            {formData.purpose === "advocacy" && (
                              <section className="contact-form-dropdown urgency-section">
                                <label>
                                  Is this matter urgent?
                                  <span className="required-star">*</span>
                                </label>
                                <div className="urgency-options">
                                  <label className={`urgency-option ${formData.urgency === 'standard' ? 'selected' : ''}`}>
                                    <input
                                      type="radio"
                                      name="urgency"
                                      value="standard"
                                      checked={formData.urgency === 'standard'}
                                      onChange={(e) => handleInputChange('urgency', e.target.value)}
                                    />
                                    <span className="urgency-radio"></span>
                                    <span className="urgency-content">
                                      <strong>No, it's not urgent</strong>
                                      <small>Response within 1-2 business days</small>
                                    </span>
                                  </label>
                                  <label className={`urgency-option urgency-high ${formData.urgency === 'urgent' ? 'selected' : ''}`}>
                                    <input
                                      type="radio"
                                      name="urgency"
                                      value="urgent"
                                      checked={formData.urgency === 'urgent'}
                                      onChange={(e) => handleInputChange('urgency', e.target.value)}
                                    />
                                    <span className="urgency-radio"></span>
                                    <span className="urgency-content">
                                      <strong>Yes, it's urgent ⚡</strong>
                                      <small>Same-day response (9am-5pm)</small>
                                    </span>
                                  </label>
                                </div>
                              </section>
                            )}

                            {/* Name */}
                            <section className="contact-form-dropdown">
                              <div>
                                <label htmlFor="contact-name">
                                  What is your full name?
                                  <span className="required-star">*</span>
                                  {errors.name && (
                                    <span className="error">{errors.name}</span>
                                  )}
                                </label>
                              </div>
                              <input
                                ref={nameInputRef}
                                id="contact-name"
                                name="name"
                                type="text"
                                placeholder="Your full name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                className={errors.name ? 'error-input' : ''}
                                autoComplete="name"
                              />
                            </section>

                            {/* Email */}
                            <section className="contact-form-dropdown">
                              <div>
                                <label htmlFor="contact-email">
                                  What is your email address?
                                  <span className="required-star">*</span>
                                  {errors.email && (
                                    <span className="error">{errors.email}</span>
                                  )}
                                </label>
                              </div>
                              <input
                                id="contact-email"
                                name="email"
                                type="email"
                                placeholder="your.email@example.com"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                className={errors.email ? 'error-input' : ''}
                                autoComplete="email"
                              />
                            </section>

                            {/* Phone (New Required Field) */}
                            <section className="contact-form-dropdown">
                              <div>
                                <label htmlFor="contact-phone">
                                  What is your phone number?
                                  <span className="required-star">*</span>
                                  {errors.phone && (
                                    <span className="error">{errors.phone}</span>
                                  )}
                                </label>
                              </div>
                              <input
                                id="contact-phone"
                                name="phone"
                                type="tel"
                                placeholder="020 3026 6258"
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                className={errors.phone ? 'error-input' : ''}
                                autoComplete="tel"
                              />
                            </section>

                            {/* Conditional: Course Interest */}
                            {formData.purpose === "enroll" && (
                              <section className="contact-form-dropdown">
                                <div>
                                  <label htmlFor="course-interest">
                                    Which course are you interested in?
                                  </label>
                                </div>
                                <input
                                  id="course-interest"
                                  name="courseInterest"
                                  type="text"
                                  placeholder="e.g., ESOL Level 1, Functional Skills Maths"
                                  value={formData.courseInterest}
                                  onChange={(e) => handleInputChange('courseInterest', e.target.value)}
                                />
                              </section>
                            )}

                            {/* Conditional: Employment Status */}
                            {formData.purpose === "employment" && (
                              <section className="contact-form-dropdown">
                                <div>
                                  <label htmlFor="employment-status">
                                    What is your current employment status?
                                    <span className="required-star">*</span>
                                    {errors.employmentStatus && (
                                      <span className="error">{errors.employmentStatus}</span>
                                    )}
                                  </label>
                                </div>
                                <select
                                  id="employment-status"
                                  name="employmentStatus"
                                  value={formData.employmentStatus}
                                  onChange={(e) => handleInputChange('employmentStatus', e.target.value)}
                                  className={errors.employmentStatus ? 'error-input' : ''}
                                >
                                  {employmentOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </section>
                            )}

                            {/* Details */}
                            <section className="contact-form-dropdown">
                              <div>
                                <label htmlFor="contact-details">
                                  Can you provide further details?
                                  <span className="required-star">*</span>
                                  {errors.details && (
                                    <span className="error">{errors.details}</span>
                                  )}
                                </label>
                              </div>
                              <textarea
                                id="contact-details"
                                name="details"
                                placeholder="Please provide as much detail as possible so we can help you effectively..."
                                value={formData.details}
                                onChange={(e) => handleInputChange('details', e.target.value)}
                                className={errors.details ? 'error-input' : ''}
                                rows={6}
                              />
                            </section>

                            {/* Privacy Agreement (New Required Field) */}
                            <section className="contact-form-dropdown privacy-section">
                              <label className={`privacy-checkbox-label ${formData.privacyAgreed ? 'checked' : ''} ${errors.privacyAgreed ? 'error' : ''}`}>
                                <input
                                  type="checkbox"
                                  name="privacyAgreed"
                                  checked={formData.privacyAgreed}
                                  onChange={(e) => handleInputChange('privacyAgreed', e.target.checked)}
                                  className="privacy-checkbox"
                                />
                                <span className="privacy-checkmark"></span>
                                <span className="privacy-text">
                                  I agree to the <Link to="/privacy-policy" target="_blank">Privacy Policy</Link> and consent to APT storing and processing my personal information to respond to my inquiry.
                                  <span className="required-star">*</span>
                                </span>
                              </label>
                              {errors.privacyAgreed && (
                                <div className="privacy-error">{errors.privacyAgreed}</div>
                              )}
                            </section>

                            {/* Submission Error */}
                            {submissionError && (
                              <section className="contact-form-dropdown">
                                <div className="error-message-box">
                                  <span className="error-icon">❌</span>
                                  <p>{submissionError}</p>
                                  <p className="error-help">
                                    If the problem persists, please call us on <a href="tel:02030266258">020 3026 6258</a>
                                  </p>
                                </div>
                              </section>
                            )}

                            {/* Submit Button */}
                            <section className="contact-form-dropdown submit-section">
                              <button
                                type="submit"
                                disabled={isLoading}
                                className={`submit-button ${isLoading ? 'loading' : ''} ${isUrgent ? 'urgent-submit' : ''}`}
                              >
                                {isLoading ? (
                                  <>
                                    <span className="spinner"></span>
                                    Sending...
                                  </>
                                ) : isUrgent ? (
                                  <>Send Urgent Message ⚡</>
                                ) : (
                                  <>Send Message</>
                                )}
                              </button>
                              
                              {isUrgent && (
                                <p className="urgent-note">
                                  <strong>Note:</strong> Our team will prioritise this message and respond today (Mon-Fri, 9am-5pm). 
                                  For immediate assistance, you can also call <a href="tel:02030266258">020 3026 6258</a>.
                                </p>
                              )}
                            </section>
                          </section>
                        </form>
                      </section>
                    </section>
                  </section>
                </section>

              </div>
            )}

            {/* FAQ Section */}
            <div className="faq-section">
              <h2 className="faq-header">Frequently Asked Questions</h2>
              <p className="faq-intro">Find quick answers to common questions:</p>
              <div className="faq-container">
                {faqs.map((faq, index) => (
                  <details key={index} className="faq-item">
                    <summary className="faq-question">{faq.question}</summary>
                    <p className="faq-answer">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </MainBodyContainer>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
